import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WarningCircle, GraduationCap } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const TrainingRadarNotice = () => {
  const { user } = useAuth();
  const [radar, setRadar] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") return;
    // Hide while the Compliance Gate is up — the gate already lists overdue trainings
    // and its overlay would swallow banner clicks (iteration_31 RCA).
    api.get("/compliance/me").then((r) => {
      if (r.data?.blocked) { setRadar({ ack_required: false }); return; }
      api.get("/training/my-radar").then((r2) => setRadar(r2.data)).catch(() => {});
    }).catch(() => {
      api.get("/training/my-radar").then((r2) => setRadar(r2.data)).catch(() => {});
    });
  }, [user?.role]);

  if (!radar || !radar.ack_required) return null;
  const urgent = (radar.groups || []).flatMap((g) => g.items).filter((i) => ["blocked", "daily", "weekly"].includes(i.tier));
  if (!urgent.length) return null;

  const ack = async () => {
    setBusy(true);
    try {
      await api.post("/training/my-radar/ack");
      setRadar({ ...radar, ack_required: false });
    } finally {
      setBusy(false);
    }
  };

  const blocked = urgent.some((i) => i.tier === "blocked");
  return (
    <div className={`border-b px-6 py-3 ${blocked ? "border-[#C0392B]/30 bg-[#C0392B]/5" : "border-[#B7791F]/30 bg-[#B7791F]/5"}`} data-testid="training-radar-notice">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3">
        {blocked ? <WarningCircle size={20} className="text-[#C0392B]" /> : <GraduationCap size={20} className="text-[#8a5d14]" />}
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${blocked ? "text-[#C0392B]" : "text-[#7c4a03]"}`} data-testid="radar-notice-title">
            {blocked ? "Training overdue — action required" : `You have ${urgent.length} training item${urgent.length === 1 ? "" : "s"} due soon`}
          </p>
          <p className="truncate text-xs text-stone-600" data-testid="radar-notice-items">
            {urgent.slice(0, 3).map((i) => `${i.title}${i.days_left != null ? ` (${i.days_left < 0 ? "overdue" : i.days_left + "d"})` : ""}`).join(" · ")}{urgent.length > 3 ? ` · +${urgent.length - 3} more` : ""}
          </p>
        </div>
        <Link to="/training" className="rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="radar-notice-open">
          Open trainings
        </Link>
        <label className="flex items-center gap-2 text-xs font-medium text-stone-700">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid="radar-ack-checkbox" />
          I acknowledge
        </label>
        <button onClick={ack} disabled={!checked || busy}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-stone-900 disabled:opacity-40" data-testid="radar-ack-button">
          {busy ? "Saving..." : "Acknowledge"}
        </button>
      </div>
    </div>
  );
};
